import React from "react";
import { View, StyleSheet, Platform } from "react-native";
import RText from "../../../../RText";
import RunnerSilk from "../../../../Racecard/RunnerSilk";
import StandardPriceButton from "../../../../Buttons/StandardPriceButton";
import {
  RacecardRunner,
  ResultRunner,
} from "../../../../../types/races/racecardAndResultWidget";
import Gap from "../../../../Gap";

/**
 * RacecardAndResultWidgetRunnerRow is a shared component used across
 * the racecard and result widgets
 *
 * It displays a single row for a runner in either the racecard or result widget\
 *
 * It's used in RacecardAndResultWidgetRunnerContainer to render each runner
 *
 * @property {boolean} isRacecard - Indicates if the widget is a racecard widget (default: true),
 * otherwise it's a result widget
 * @property {boolean} isDetailedWidget - Indicates if the widget is in detailed mode (default: false)
 * @property {object} data - Object containing runner information
 *
 * @returns {JSX.Element} The RacecardAndResultWidgetRunnerRow component
 */

type Props = {
  isRacecard?: boolean;
  isDetailedWidget?: boolean;
  data: RacecardRunner | ResultRunner;
};

const RacecardAndResultWidgetRunnerRow = ({
  isRacecard = true,
  isDetailedWidget = false,
  data,
}: Props) => {
  const isWeb = Platform.OS === "web";

  const racecardData = isRacecard ? (data as RacecardRunner) : null;
  const resultData = !isRacecard ? (data as ResultRunner) : null;

  const finishText = resultData?.finish_text ?? "";
  const clothNumber = data?.cloth_number ?? "";
  const horseName = racecardData?.horse_name ?? resultData?.horse?.name ?? "";
  const jockeyName =
    racecardData?.jockey_name ?? resultData?.jockey?.name ?? "";
  const barrierNumber = racecardData?.barrier_number ?? "";
  const isWinner = resultData?.finish_place === 1;

  const isResult = !isRacecard;

  //if detailed result widget, increase size of silk and display jockey name
  const isDetailedResultWidget = isDetailedWidget && isResult;

  const hasNumbers = !!(clothNumber || barrierNumber);

  return (
    <View
      style={[
        styles.container,
        isDetailedWidget ? { minHeight: 35 } : { minHeight: 32 },
      ]}
    >
      <View style={styles.containerLeft}>
        {hasNumbers && (
          <View style={[styles.runnerNumbers]}>
            <View
              style={isRacecard ? { minWidth: 16 } : styles.runnerNumbersResult}
            >
              <RText
                size={isRacecard ? "sm" : "sm_md"}
                weight={isRacecard || isWinner ? "bold" : "regular"}
                //the below styles stop the finish text from making the box
                // too tall on all platforms (additional style needed to
                // re-position text on iOS)
                lineHeight={isRacecard ? 15 : 18}
                //this is to account for the slightly different font size in the apps to keep the text vertically centered
                style={{ top: isWeb ? 0 : 1 }}
              >
                {isRacecard ? clothNumber : finishText}
              </RText>
            </View>
            {!!barrierNumber && isRacecard && (
              <RText size="sm" lineHeight={15}>
                {`(${barrierNumber})`}
              </RText>
            )}
          </View>
        )}
        <View style={styles.marginRight}>
          <RunnerSilk
            size={isDetailedResultWidget ? "large" : "medium"}
            silk={data?.silk}
          />
        </View>
        <View style={styles.flex}>
          <RText
            // This is to accommodate really long, uncommon horse names,
            // to keep them on one line and without changing the size of the overall widget
            size={horseName.length < 19 ? "sm" : "xs"}
            weight={
              isRacecard || isWinner || isDetailedResultWidget
                ? "bold"
                : "regular"
            }
            lineHeight={18}
            numberOfLines={1}
          >
            {isResult ? `${clothNumber} ` : ""}
            {horseName}
          </RText>
          {isDetailedResultWidget && jockeyName ? (
            <>
              <Gap vertical style={{ minHeight: 4, maxHeight: 4 }} />
              <RText size="xs" lineHeight={13}>
                J: {jockeyName}
              </RText>
            </>
          ) : null}
        </View>
      </View>
      {isRacecard ? (
        <StandardPriceButton odd={data?.bestOdds} />
      ) : (
        <RText size="md" weight={isWinner ? "bold" : "regular"}>
          {data?.starting_price?.fractional}
        </RText>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  containerLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  runnerNumbers: {
    alignItems: "center",
    paddingRight: 8,
  },
  runnerNumbersResult: {
    borderWidth: 0.5,
    borderColor: "#181821",
    borderStyle: "solid",
    borderRadius: 2,
    paddingHorizontal: 4,
    width: 35,
  },
  marginRight: {
    marginRight: 8,
  },
  flex: {
    flex: 1,
  },
});

export default RacecardAndResultWidgetRunnerRow;
